import {Button, CircularProgress, Divider, Stack, Typography} from '@mui/material';
import {useObservable} from '@ngneat/react-rxjs';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {finalize, map} from 'rxjs';

import {Indexation, indexationService, IndexationType, ManageIndexation} from '@store/settings/indexation';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';
import {serviceKindsQuery, serviceKindsService, ServiceKindTypeEnum} from '@store/settings/serviceKinds';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import AsyncMultiSelectWithSearchComponent
  from '@components/inputs/asyncMultiSelect/AsyncMultiSelectWithSearch.component';
import MultiSelectComponent from '@components/inputs/multiSelect/MultiSelect.component';
import ModalComponent from '@components/modal/Modal.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';

interface IndexationDetailsModalProps {
  indexation: Indexation;
  handleClose: () => void;
}

const IndexationDetailsModal = (props: IndexationDetailsModalProps) => {
  const {indexation, handleClose} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [manageIndexation, setManageIndexation] = useState<ManageIndexation>({});
  const [clientOrganisationItems, setClientOrganisationItems] = useState<SelectItem[]>([]);
  const [supplierOrganisationItems, setSupplierOrganisationItems] = useState<SelectItem[]>([]);

  const [{serviceKinds}] = useObservable(serviceKindsQuery.serviceKinds$);

  useEffect(() => {
    serviceKindsService.getServiceKinds().subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  }, [enqueueSnackbar]);

  useEffect(() => {
    indexationService.getIndexation(indexation.id.toString()).subscribe({
      next: (data) => {
        setManageIndexation(Indexation.toManageIndexation(data));
        setClientOrganisationItems(
          (data.clientOrganisations ?? []).map((o) => ({label: o.name, value: o.id}))
        );
        setSupplierOrganisationItems(
          (data.supplierOrganisations ?? []).map((o) => ({label: o.name, value: o.id}))
        );
        setLoading(false);
      },
      error: (err) => {
        enqueueSnackbar(err.text, err.options);
        setLoading(false);
      },
    });
  }, [indexation.id, enqueueSnackbar]);

  const update = (name: string) => (value: any) => setManageIndexation((state) => ({...state, [name]: value}));

  const errors = {
    name: getFieldError(manageIndexation.name, FieldValidationType.REQUIRED_STRING),
    invoiceName: getFieldError(manageIndexation.invoiceName, FieldValidationType.REQUIRED_STRING),
    code: getFieldError(manageIndexation.code, FieldValidationType.REQUIRED_STRING),
    serviceKindId: getFieldError(manageIndexation.serviceKindId, FieldValidationType.REQUIRED_NUMBER),
    type: getFieldError(manageIndexation.type, FieldValidationType.REQUIRED_STRING),
  };

  const handleSave = () => {
    setSaveLoading(true);
    indexationService.saveIndexation(indexation.id, {
      name: manageIndexation.name,
      invoiceName: manageIndexation.invoiceName,
      code: manageIndexation.code,
      serviceKindId: manageIndexation.serviceKindId,
      serviceKindTypes: manageIndexation.serviceKindTypes,
      type: manageIndexation.type,
      clientOrganisationIds: manageIndexation.type === IndexationType.SELL
        ? clientOrganisationItems.map((item) => item.value as number)
        : [],
      supplierOrganisationIds: manageIndexation.type === IndexationType.BUY
        ? supplierOrganisationItems.map((item) => item.value as number)
        : [],
    }).pipe(finalize(() => setSaveLoading(false)))
      .subscribe({
        next: () => {
          enqueueSnackbar(t('global.success'), {variant: 'success'});
          handleClose();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <ModalComponent
      title={t('settings.indexation.drawer.title')}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="2rem">
          {loading && (
            <Stack flex={1} alignItems="center" justifyContent="center">
              <CircularProgress/>
            </Stack>
          )}
          {!loading && (
            <>
              <TextFieldComponent
                color="secondary"
                labelWidth={180}
                label={t('settings.indexation.drawer.fields.name')}
                placeholder={t('global.writeSomething')}
                handleChange={update('name')}
                value={manageIndexation.name || ''}
                error={errors.name}
                required/>
              <TextFieldComponent
                color="secondary"
                labelWidth={180}
                label={t('settings.indexation.drawer.fields.invoiceName')}
                placeholder={t('global.writeSomething')}
                handleChange={update('invoiceName')}
                value={manageIndexation.invoiceName || ''}
                error={errors.invoiceName}
                required/>
              <TextFieldComponent
                color="secondary"
                labelWidth={180}
                label={t('settings.indexation.drawer.fields.code')}
                placeholder={t('global.writeSomething')}
                handleChange={update('code')}
                value={manageIndexation.code || ''}
                error={errors.code}
                required/>
              <SelectComponent
                color="secondary"
                labelWidth={180}
                label={t('settings.indexation.drawer.fields.serviceKind')}
                placeholder={t('global.selectOption')}
                handleChange={update('serviceKindId')}
                value={manageIndexation.serviceKindId}
                error={errors.serviceKindId}
                items={serviceKinds.map((sk) => ({value: sk.id, label: sk.kind}))}
                required/>
              <Divider variant="fullWidth" orientation="horizontal" sx={{paddingTop: '16px'}}/>
              <Typography fontSize={16} fontWeight="700" pt="8px">
                {t('settings.indexation.drawer.sections.associations')}
              </Typography>
              <SelectComponent
                color="secondary"
                labelWidth={180}
                label={t('settings.indexation.drawer.fields.type')}
                placeholder={t('global.selectOption')}
                handleChange={update('type')}
                value={manageIndexation.type}
                error={errors.type}
                items={IndexationType.selectItems()}
                required/>
              <MultiSelectComponent
                color="secondary"
                labelWidth={180}
                label={t('settings.indexation.drawer.fields.serviceKindTypes')}
                placeholder={t('global.selectOption')}
                handleChange={(values) => update('serviceKindTypes')(values as ServiceKindTypeEnum[])}
                values={manageIndexation.serviceKindTypes ?? []}
                items={ServiceKindTypeEnum.selectItems()}/>
              {manageIndexation.type === IndexationType.SELL && (
                <AsyncMultiSelectWithSearchComponent
                  color="secondary"
                  labelWidth={180}
                  label={t('settings.indexation.drawer.fields.clientOrganisations')}
                  placeholder={t('global.selectOption')}
                  getOptions={(search) => filtersService
                    .getFilters(TableFilterType.CLIENT_ORGANISATION, FilterBaseEntityEnum.NONE, [], search)
                    .pipe(map((options) => options.map((o) => ({label: o.name || '-', value: o.id}))))}
                  values={clientOrganisationItems}
                  handleChange={setClientOrganisationItems}/>
              )}
              {manageIndexation.type === IndexationType.BUY && (
                <AsyncMultiSelectWithSearchComponent
                  color="secondary"
                  labelWidth={180}
                  label={t('settings.indexation.drawer.fields.supplierOrganisations')}
                  placeholder={t('global.selectOption')}
                  getOptions={(search) => filtersService
                    .getFilters(TableFilterType.SUPPLIER_ORGANISATION, FilterBaseEntityEnum.NONE, [], search)
                    .pipe(map((options) => options.map((o) => ({label: o.name || '-', value: o.id}))))}
                  values={supplierOrganisationItems}
                  handleChange={setSupplierOrganisationItems}/>
              )}
            </>
          )}
        </Stack>
      )}
      actions={!loading ? (
        <Button
          variant="contained"
          color="primary"
          disabled={saveLoading || checkIfErrors(errors)}
          onClick={handleSave}>
          {t('global.save')}
        </Button>
      ) : undefined}
    />
  );
};

export default IndexationDetailsModal;